import { useState } from 'react';
import { Loader2, Play, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { diagnosticsAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Diagnostic panel: decomposes the gap between Total Issued (all client
 * invoices, incl. VAT, all dates) and Total Invoiced (non-voided invoices,
 * excl. VAT, period-scoped) into a component waterfall with a residual.
 */
export function IssuedVsInvoicedWaterfallPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.issuedVsInvoicedWaterfall({
        period: 'ytd',
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const dc = 'GBP';

  // Residual materiality: warn if absolute value exceeds 1.00
  const residualRow = result?.waterfall?.find((r) => r.component === 'residual');
  const residualAbs = residualRow ? Math.abs(residualRow.amount ?? 0) : 0;
  const residualMaterial = residualAbs > 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Issued vs Invoiced: Gap Waterfall
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Decomposes the gap between Total Issued (all 319 client invoices,
          incl. VAT, all dates) and Total Invoiced (non-voided invoices,
          excl. VAT, period-scoped). Scope note: Issued counts all client
          invoices all-dates gross; Invoiced counts non-voided period-scoped
          net.
        </p>

        <Button onClick={run} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Play className="h-4 w-4 mr-2" />
          )}
          Run Waterfall
        </Button>

        {result && (
          <div className="space-y-4">
            {/* Source totals */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-xs text-muted-foreground">Total Issued</p>
                <p className="text-lg font-bold font-mono tabular-nums">
                  {formatCurrency(result.total_issued, dc)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {result.total_issued_count} invoices, {result.total_issued_basis === 'incl_vat' ? 'incl. VAT' : 'excl. VAT'}
                </p>
                <p className="text-xs text-muted-foreground italic">
                  {result.total_issued_scope}
                </p>
              </div>

              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-xs text-muted-foreground">Total Invoiced</p>
                <p className="text-lg font-bold font-mono tabular-nums">
                  {formatCurrency(result.total_invoiced, dc)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {result.total_invoiced_count} invoices, {result.total_invoiced_basis === 'excl_vat' ? 'excl. VAT' : 'incl. VAT'}
                </p>
                <p className="text-xs text-muted-foreground italic">
                  {result.total_invoiced_scope}
                </p>
              </div>

              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-xs text-muted-foreground">Gap</p>
                <p className="text-lg font-bold font-mono tabular-nums">
                  {formatCurrency(result.gap, dc)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Issued minus Invoiced
                </p>
              </div>
            </div>

            {/* Waterfall table */}
            <div className="overflow-x-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Component</TableHead>
                    <TableHead className="text-right">Amount ({dc})</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(result.waterfall ?? []).map((row) => {
                    const isResidual = row.component === 'residual';
                    const isMaterial = isResidual && residualMaterial;
                    return (
                      <TableRow
                        key={row.component}
                        className={isMaterial ? 'bg-amber-50 dark:bg-amber-950/30' : ''}
                      >
                        <TableCell className="font-medium text-sm">
                          <div className="flex items-center gap-2">
                            {row.label}
                            {isResidual && isMaterial && (
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-sm">
                          {formatCurrency(row.amount ?? 0, dc)}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-sm">
                          {row.count ?? 0}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[300px]">
                          {row.note}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Unmatched items detail (if any) */}
            {(() => {
              const unmatchedRow = result.waterfall?.find(
                (r) => r.component === 'unmatched_invoices',
              );
              const items = unmatchedRow?.items;
              if (!items?.length) return null;
              return (
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    Unmatched invoice detail ({items.length})
                  </p>
                  <div className="overflow-x-auto border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice</TableHead>
                          <TableHead>Kind</TableHead>
                          <TableHead>Orphan booking</TableHead>
                          <TableHead className="text-right">Amount ({dc})</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item, idx) => (
                          <TableRow key={item.invoice_id ?? idx}>
                            <TableCell className="font-mono text-xs">{item.invoice_id}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {item.kind}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {Object.prototype.hasOwnProperty.call(item, 'orphan_booking_id')
                                ? item.orphan_booking_id
                                : ''}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {formatCurrency(item.total_amount_gbp ?? 0, dc)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              );
            })()}

            {/* Identity check */}
            <div className="flex items-center gap-2 text-sm">
              {result.identity_check?.holds ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span>Identity check passed: components + residual = gap</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="text-red-600 font-medium">
                    Identity check FAILED: components + residual (
                    {formatCurrency(result.identity_check?.components_plus_residual ?? 0, dc)})
                    {' '} != gap ({formatCurrency(result.identity_check?.gap ?? 0, dc)})
                  </span>
                </>
              )}
            </div>

            {/* Residual warning */}
            {residualMaterial && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded text-sm">
                <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4" />
                  Material residual: {formatCurrency(residualRow?.amount ?? 0, dc)} ({residualRow?.count ?? 0} contributing invoices)
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-500 mt-1">
                  A material residual may indicate a real under-billing. Read together
                  with ENQ-00185 (known genuine under-billing of -4,337.02) before
                  concluding anything new.
                </p>
              </div>
            )}

            {/* Timestamp */}
            {result.checked_at && (
              <p className="text-xs text-muted-foreground">
                Checked: {new Date(result.checked_at).toLocaleString('en-GB')}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
